import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import BaseDialog from '../BaseDialog';
import useApiControl from '../../../common/useApiControl';

const formatErrors = (errors) => {
  if (!errors) {
    return '';
  }

  if (typeof errors === 'string') {
    return errors;
  }

  if (Array.isArray(errors)) {
    return errors.join(' / ');
  }

  if (typeof errors === 'object') {
    return Object.entries(errors)
      .map(([field, fieldErrors]) => {
        const detail = Array.isArray(fieldErrors)
          ? fieldErrors.join('、')
          : fieldErrors;
        return `${field}: ${detail}`;
      })
      .join(' / ');
  }

  return '';
};

const getErrorMessage = (error) => {
  if (!error) {
    return '';
  }

  const responseData = error.response?.data;

  if (responseData) {
    if (responseData.message) {
      return responseData.message;
    }

    const validationMessage = formatErrors(responseData.errors);
    if (validationMessage) {
      return validationMessage;
    }

    const errorMessage = formatErrors(responseData.error);
    if (errorMessage) {
      return errorMessage;
    }

    if (typeof responseData === 'string') {
      return responseData;
    }
  }

  return error.message || 'エラーが発生しました。';
};

const GuestRegisterDialog = ({ isVisible, onRegistered, onClose }) => {
  const [step, setStep] = useState('welcome');
  const [playerName, setPlayerName] = useState('');
  const [message, setMessage] = useState('');

  const { loading, executeRequest } = useApiControl({
    action: '/register',
    method: 'POST',
  });

  useEffect(() => {
    if (isVisible) {
      setStep('welcome');
      setPlayerName('');
      setMessage('');
    }
  }, [isVisible]);

  const title = useMemo(() => {
    if (step === 'name') {
      return 'プレイヤー名入力';
    }

    if (step === 'complete') {
      return 'ようこそ';
    }

    return 'Bettyへようこそ';
  }, [step]);

  const handleRegister = () => {
    setMessage('');

    executeRequest({ name: playerName }).then((response) => {
      const { user, token } = response;
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      setStep('complete');
      onRegistered(response);
    }).catch((error) => {
      setMessage(getErrorMessage(error));
    });
  };

  const handleLogin = () => {
    window.location.href = '/login';
  };

  return (
    <BaseDialog isVisible={isVisible} onClose={onClose} clickOutsideToClose={false}>
      <Stack spacing={3} sx={{ width: 360, maxWidth: '100%' }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>

        {step === 'welcome' && (
          <>
            <Typography sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
              {'Bettyは、みんなで結果を予想してポイントを競う参加型ゲームです。\nまずは気になる部屋に参加して、どちらの結果になるか予想してみましょう。'}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="contained" onClick={() => setStep('name')}>
                はじめる
              </Button>
              <Button size="small" variant="outlined" onClick={handleLogin}>
                アカウントをお持ちの方
              </Button>
            </Stack>
          </>
        )}

        {step === 'name' && (
          <>
            <Typography>
              あなたの名前を教えてください
            </Typography>
            <TextField
              label="Bettyでのあなたの名前を入力してください"
              variant="outlined"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              autoFocus
              fullWidth
            />
            {message && <Alert severity="error">{message}</Alert>}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => setStep('welcome')}>
                戻る
              </Button>
              <Button variant="contained" onClick={handleRegister} disabled={loading}>
                {loading ? '登録中...' : '次へ'}
              </Button>
            </Stack>
          </>
        )}

        {step === 'complete' && (
          <>
            <Typography sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
              {'準備ができました。\n気になるルームを見つけたら、クリック(タップ)してください。\n「参加する」を押すと、予想に参加できます。'}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="contained" onClick={onClose}>
                はじめる
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </BaseDialog>
  );
};

export default GuestRegisterDialog;
